<template>
  <v-container class="py-6" max-width="800">
    <!-- Header -->
    <div class="d-flex align-center mb-1">
      <v-btn icon variant="text" to="/admin" size="small" class="mr-2">
        <v-icon>mdi-arrow-left</v-icon>
      </v-btn>
      <v-icon color="warning" size="28" class="mr-3">mdi-cog-outline</v-icon>
      <h1 class="text-h5 font-weight-bold">Indstillinger</h1>
    </div>
    <p class="text-body-2 text-medium-emphasis mb-6 ml-12">Konfigurer platformens indstillinger</p>

    <!-- General Settings -->
    <v-card variant="outlined" class="mb-4">
      <v-card-title class="text-body-1 font-weight-bold d-flex align-center">
        <v-icon class="mr-2" size="20">mdi-web</v-icon>
        Generelt
      </v-card-title>
      <v-divider />
      <v-card-text>
        <v-text-field
          v-model="settings.siteName"
          label="Sidenavn"
          variant="outlined"
          density="compact"
          class="mb-3"
        />
        <v-textarea
          v-model="settings.siteDescription"
          label="Beskrivelse"
          variant="outlined"
          density="compact"
          rows="2"
          class="mb-3"
        />
        <v-text-field
          v-model="settings.supportEmail"
          label="Support email"
          variant="outlined"
          density="compact"
        />
      </v-card-text>
    </v-card>

    <!-- Registration & Access -->
    <v-card variant="outlined" class="mb-4">
      <v-card-title class="text-body-1 font-weight-bold d-flex align-center">
        <v-icon class="mr-2" size="20">mdi-account-key</v-icon>
        Registrering & Adgang
      </v-card-title>
      <v-divider />
      <v-card-text>
        <v-switch
          v-model="settings.registrationEnabled"
          label="Tillad nye registreringer"
          color="primary"
          density="compact"
          hide-details
          class="mb-3"
        />
        <v-switch
          v-model="settings.emailVerificationRequired"
          label="Kræv email-bekræftelse"
          color="primary"
          density="compact"
          hide-details
          class="mb-3"
        />
        <v-switch
          v-model="settings.googleLoginEnabled"
          label="Google login aktiveret"
          color="primary"
          density="compact"
          hide-details
          class="mb-3"
        />
        <v-switch
          v-model="settings.appleLoginEnabled"
          label="Apple login aktiveret"
          color="primary"
          density="compact"
          hide-details
        />
      </v-card-text>
    </v-card>

    <!-- Subscription Settings -->
    <v-card variant="outlined" class="mb-4">
      <v-card-title class="text-body-1 font-weight-bold d-flex align-center">
        <v-icon class="mr-2" size="20">mdi-credit-card-settings</v-icon>
        Abonnement
      </v-card-title>
      <v-divider />
      <v-card-text>
        <v-text-field
          v-model.number="settings.trialDays"
          label="Prøveperiode (dage)"
          type="number"
          variant="outlined"
          density="compact"
          class="mb-3"
        />
        <v-text-field
          v-model.number="settings.monthlyPrice"
          label="Månedlig pris (DKK)"
          type="number"
          variant="outlined"
          density="compact"
          class="mb-3"
        />
        <v-text-field
          v-model.number="settings.yearlyPrice"
          label="Årlig pris (DKK)"
          type="number"
          variant="outlined"
          density="compact"
        />
      </v-card-text>
    </v-card>

    <!-- Game Settings -->
    <v-card variant="outlined" class="mb-6">
      <v-card-title class="text-body-1 font-weight-bold d-flex align-center">
        <v-icon class="mr-2" size="20">mdi-dice-multiple</v-icon>
        Spilindstillinger
      </v-card-title>
      <v-divider />
      <v-card-text>
        <v-text-field
          v-model.number="settings.maxGamesPerFreeUser"
          label="Maks spil for gratis brugere"
          type="number"
          variant="outlined"
          density="compact"
          class="mb-3"
        />
        <v-switch
          v-model="settings.maintenanceMode"
          label="Vedligeholdelsestilstand"
          color="error"
          density="compact"
          hide-details
        />
      </v-card-text>
    </v-card>

    <!-- Save -->
    <div class="d-flex justify-end ga-3">
      <v-btn variant="text" @click="loadSettings">Nulstil</v-btn>
      <v-btn variant="flat" color="primary" :loading="saving" @click="saveSettings">
        <v-icon class="mr-1">mdi-content-save</v-icon>
        Gem indstillinger
      </v-btn>
    </div>

    <!-- Snackbar -->
    <v-snackbar v-model="snackbar" :color="snackColor" timeout="3000">
      {{ snackMessage }}
    </v-snackbar>
  </v-container>
</template>

<script setup>
import { ref, reactive, onMounted } from "vue";
import { getSettings, updateSettings } from "../../services/adminApi";

const saving = ref(false);
const snackbar = ref(false);
const snackMessage = ref("");
const snackColor = ref("success");

const settings = reactive({
  siteName: "Backgammon Tracker",
  siteDescription: "",
  supportEmail: "",
  registrationEnabled: true,
  emailVerificationRequired: true,
  googleLoginEnabled: true,
  appleLoginEnabled: false,
  trialDays: 14,
  monthlyPrice: 49,
  yearlyPrice: 399,
  maxGamesPerFreeUser: 10,
  maintenanceMode: false,
});

async function loadSettings() {
  try {
    const { data } = await getSettings();
    if (data.settings) {
      Object.assign(settings, data.settings);
    }
  } catch {
    // API not implemented yet – defaults are fine
  }
}

async function saveSettings() {
  saving.value = true;
  try {
    await updateSettings({ ...settings });
    snackColor.value = "success";
    snackMessage.value = "Indstillinger gemt!";
    snackbar.value = true;
  } catch (err) {
    snackColor.value = "error";
    snackMessage.value = "Fejl: " + (err.response?.data?.error || err.message);
    snackbar.value = true;
  }
  saving.value = false;
}

onMounted(loadSettings);
</script>